import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, onMounted, ref } from 'vue';
import { Modal, notification } from 'ant-design-vue';
import store from '@/store';
import i18n from '@/i18n';
import { UserRightsEnum } from '@/enums/global/UserRights';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import StatusEvent from '@/models/StatusEvent';
import NextStepTemplate from '@/models/NextStepTemplate';
import Label from '@/models/Label';
import CMSUser from '@/models/CMSUser';
import NextStepType from '@/models/NextStepType';
import AddNewStatusEvent from '@/modules/CMS/components/AddNewStatusEvent.vue';
import { formatDate } from '@/helpers/CmsIndexHelper';
import StatusEventRepository from '@/repositories/StatusEventRepository';
import NextStepTemplateRepository from '@/repositories/NextStepTemplateRepository';
import LabelReporistory from '@/repositories/LabelReporistory';
import CMSUserRepository from '@/repositories/CMSUserRepository';
import NextStepTypeRepository from '@/repositories/NextStepTypeRepository';
import SettingsTopBar from '@/modules/CMS/layout/SettingsTopBar.vue';
import type ActivityFormConfiguration from '@/models/interfaces/ActivityFormConfiguration';
import type StatusEventConfiguration from '@/models/interfaces/StatusEventConfiguration';
import type { StatusEventFormReference } from '@/modules/CMS/interfaces/StatusEventFormReference';
import type { StatusEventFormSubmission } from '@/modules/CMS/interfaces/StatusEventFormSubmission';


export default /*#__PURE__*/_defineComponent({
  __name: 'AutomaticEvents',
  setup(__props) {

const { proxy } = getCurrentInstance()!;
const loadingOverlay = new LoadingOverlayHelper(proxy, {});

const activeStatus = ref('ACTIVE');
const searchQuery = ref('');
const selectedRowKeys = ref<string[]>([]);
const isNewStatusModalVisible = ref(false);
const isExistingStatus = ref(false);
const statusEventId = ref<string | null>(null);
const pendingCreatedStatusEventId = ref<string | null>(null);
const nextStepTemplateId = ref<string | null>(null);
const statusEventFormData = ref<StatusEventConfiguration | null>(null);
const activityTemplateFormData = ref<ActivityFormConfiguration | null>(null);
const statusEventForm = ref<StatusEventFormReference | null>(null);
const responsiblePersonTypes = [
    { name: 'CREATOR', id: 'CREATOR' },
    { name: 'COMMERCIALIST', id: 'COMMERCIALIST' },
];

const columns = computed(() => [
    { title: i18n.t('Name'), dataIndex: 'name', key: 'name' },
    { title: i18n.t('Status'), dataIndex: 'status', key: 'status' },
    { title: i18n.t('Action'), dataIndex: 'type', key: 'type' },
    { title: i18n.t('Creator'), dataIndex: 'creator', key: 'creator' },
    { title: i18n.t('Date created'), dataIndex: 'createdAt', key: 'createdAt' },
]);

const statuses = computed<any[]>(() =>
    StatusEventRepository.getAll().map((statusEvent: StatusEvent) => ({
        key: statusEvent.id,
        name: statusEvent.name,
        status: statusEvent.status,
        type: statusEvent.type,
        creator: statusEvent.madeByUser,
        createdAt: formatDate(statusEvent.createdAt),
    })),
);

const allLabels = computed(() => LabelReporistory.getAll());
const activityTypes = computed(() => NextStepTypeRepository.getAll());
const activeUsers = computed(() => [...responsiblePersonTypes, ...CMSUserRepository.getAll()]);

// Filter data by archived state and search query
const filteredStatuses = computed(() =>
    statuses.value.filter((status: StatusEventConfiguration) => {
        return (
            status.status === activeStatus.value &&
            status?.name?.toLowerCase().includes(searchQuery.value.toLowerCase())
        );
    }),
);

const userRights = computed(() => store.getters['jwtData/userRights']);
const userRoles = computed(() => store.getters['jwtData/userRoles']);
const canEditUserRights = computed(() => userRights.value.includes(UserRightsEnum.EDIT_USER_RIGHTS));
const isSuperAdmin = computed(() => userRoles.value.includes('ROLE_SUPER_ADMIN'));

function addNew() {
    if (!isSuperAdmin.value) {
        notification.error({
            message: i18n.t('Error creating event') as string,
            description: i18n.t('You do not have the rights to create event statuses') as string,
        });

        return;
    }

    resetStatusEventForm();
    isNewStatusModalVisible.value = true;
}

function submitStatusEventForm() {
    if (!isSuperAdmin.value) {
        notification.error({
            message: i18n.t(isExistingStatus.value ? 'Error updating event' : 'Error creating event') as string,
            description: i18n.t(
                isExistingStatus.value
                    ? 'You do not have the rights to update event statuses'
                    : 'You do not have the rights to create event statuses',
            ) as string,
        });

        return;
    }

    statusEventForm.value?.submit();
}

async function saveStatusEvent({ statusEvent, activityTemplate }: StatusEventFormSubmission) {
    if (!validateStatusEvent(statusEvent)) {
        return;
    }

    const shouldSaveActivityTemplate = statusEvent.type === 'POPUP';

    if (shouldSaveActivityTemplate && !validateActivityTemplate(activityTemplate)) {
        return;
    }

    const isUpdating = Boolean(statusEventId.value);
    loadingOverlay.start();

    try {
        let savedStatusEventId: string;

        if (statusEventId.value) {
            savedStatusEventId = statusEventId.value;
            await StatusEvent.updateExisting(savedStatusEventId, statusEvent);
        } else if (pendingCreatedStatusEventId.value) {
            savedStatusEventId = pendingCreatedStatusEventId.value;
            await StatusEvent.updateExisting(savedStatusEventId, statusEvent);
        } else {
            const fetchedEvent = await StatusEvent.createNew(statusEvent);
            savedStatusEventId = String(fetchedEvent.data.id);
            pendingCreatedStatusEventId.value = savedStatusEventId;
        }

        if (shouldSaveActivityTemplate) {
            const activityTemplatePayload = {
                ...activityTemplate,
                statusEventId: savedStatusEventId,
            };

            if (nextStepTemplateId.value) {
                await NextStepTemplate.updateExisting(nextStepTemplateId.value, activityTemplatePayload);
            } else {
                await NextStepTemplate.createNew(activityTemplatePayload);
            }
        }

        pendingCreatedStatusEventId.value = null;

        notification.success({
            message: i18n.t(
                isUpdating ? 'Status event updated!' : 'Status event configuration successfully created!',
            ) as string,
            description: '',
        });

        closePopup();
    } catch (err) {
        notification.error({
            message: i18n.t(
                isUpdating ? 'Error while updating status event!' : 'Error while creating status event configuration!',
            ) as string,
            description: '',
        });
    } finally {
        loadingOverlay.stop();
    }
}

function validateStatusEvent(statusEvent: StatusEventConfiguration) {
    if (!statusEvent.name) {
        notification.error({
            message: i18n.t('Please add a name!') as string,
            description: '',
        });
        return false;
    }

    if (!statusEvent.receptionUsers?.length && statusEvent.type === 'EMAIL') {
        notification.error({
            message: i18n.t('Please add a user!') as string,
            description: '',
        });
        return false;
    }

    if (!statusEvent.labelId) {
        notification.error({
            message: i18n.t('Please select a label!') as string,
            description: '',
        });
        return false;
    }

    if (statusEvent.conditions?.selectedTimeStep === null) {
        notification.error({
            message: i18n.t('Please add interval!') as string,
            description: '',
        });
        return false;
    }

    if (statusEvent.conditions?.selectedTimeStep !== 0 && !statusEvent.conditions?.timeUnit) {
        notification.error({
            message: i18n.t('Please add time unit!') as string,
            description: '',
        });
        return false;
    }

    return true;
}

function validateActivityTemplate(activityTemplate: ActivityFormConfiguration) {
    if (!activityTemplate.activityType) {
        notification.error({
            message: i18n.t('Please add an activity type!') as string,
            description: '',
        });
        return false;
    }

    if (!activityTemplate.responsiblePersonType) {
        notification.error({
            message: i18n.t('Please assign a user to activity!') as string,
            description: '',
        });
        return false;
    }

    return true;
}

// Open modal and pass data to it
async function navigateToEvent(id: string) {
    loadingOverlay.start();

    try {
        await StatusEvent.getById(id);
        const statusEvent = StatusEventRepository.getById(id) as StatusEventConfiguration | null;

        if (!statusEvent) {
            throw new Error('Status event was not found.');
        }

        nextStepTemplateId.value = null;
        activityTemplateFormData.value = null;

        if (statusEvent.type === 'POPUP' && statusEvent.nextStepTemplate) {
            const fetchedNextStepTemplateId = String(statusEvent.nextStepTemplate);
            nextStepTemplateId.value = fetchedNextStepTemplateId;
            await NextStepTemplate.getById(fetchedNextStepTemplateId);

            const nextStepTemplate = NextStepTemplateRepository.getById(fetchedNextStepTemplateId);

            if (nextStepTemplate) {
                activityTemplateFormData.value = {
                    activityType: nextStepTemplate.type.id,
                    name: nextStepTemplate.name,
                    note: nextStepTemplate.note,
                    responsiblePersonType: nextStepTemplate.responsiblePersonType,
                };
            }
        }

        const label = allLabels.value.find((labelItem) => labelItem.name === statusEvent.projectLabel);

        statusEventFormData.value = {
            name: statusEvent.name,
            labelName: statusEvent.projectLabel,
            labelId: label ? label.id : null,
            status: statusEvent.status,
            statusChange: statusEvent.statusChange,
            type: statusEvent.type,
            conditions: { ...statusEvent.conditions! },
            receptionUsers: statusEvent.receptionUsers,
        };
        statusEventId.value = id;
        isExistingStatus.value = true;
        isNewStatusModalVisible.value = true;
    } catch (error) {
        notification.error({
            message: i18n.t('Error while fetching status event!') as string,
            description: '',
        });
    } finally {
        loadingOverlay.stop();
    }
}

function confirmDelete() {
    if (!isSuperAdmin.value) {
        notification.error({
            message: i18n.t('Error deleting event') as string,
            description: i18n.t('You do not have the rights to delete event statuses') as string,
        });

        return;
    }

    if (!selectedRowKeys.value.length) {
        notification.error({
            message: i18n.t('Please select at least one row to delete') as string,
            description: '',
        });
        return;
    }

    Modal.confirm({
        title: 'Are you sure you want to delete selected events?',
        content: 'This action cannot be undone.',
        okText: 'Yes',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => {
            deleteStatusEvent();
        },
    });
}

async function deleteStatusEvent() {
    loadingOverlay.start();

    try {
        await Promise.all(selectedRowKeys.value.map((id) => StatusEvent.deleteExisting(id)));

        notification.success({
            message: i18n.t('Event configuration successfully deleted!') as string,
            description: '',
        });
    } catch (error) {
        notification.error({
            message: i18n.t('Error deleting report') as string,
            description: (error as Error).message,
        });
        return Promise.reject(error);
    } finally {
        loadingOverlay.stop();
        selectedRowKeys.value = [];
    }

    return Promise.resolve();
}

function onSelectChange(rowKeys: any) {
    selectedRowKeys.value = rowKeys;
}

function closePopup() {
    isNewStatusModalVisible.value = false;
    resetStatusEventForm();
}

function resetStatusEventForm() {
    isExistingStatus.value = false;
    statusEventId.value = null;
    pendingCreatedStatusEventId.value = null;
    nextStepTemplateId.value = null;
    statusEventFormData.value = null;
    activityTemplateFormData.value = null;
}

onMounted(async () => {
    loadingOverlay.start();

    try {
        await Promise.all([StatusEvent.getAll(), Label.getAll(), CMSUser.getAll(), NextStepType.getAll()]);
    } catch (error) {
        notification.error({
            message: i18n.t('Error while loading automatic events data!') as string,
            description: '',
        });
    } finally {
        loadingOverlay.stop();
    }
});

return { __sfc: true,proxy, loadingOverlay, activeStatus, searchQuery, selectedRowKeys, isNewStatusModalVisible, isExistingStatus, statusEventId, pendingCreatedStatusEventId, nextStepTemplateId, statusEventFormData, activityTemplateFormData, statusEventForm, responsiblePersonTypes, columns, statuses, allLabels, activityTypes, activeUsers, filteredStatuses, userRights, userRoles, canEditUserRights, isSuperAdmin, addNew, submitStatusEventForm, saveStatusEvent, validateStatusEvent, validateActivityTemplate, navigateToEvent, confirmDelete, deleteStatusEvent, onSelectChange, closePopup, resetStatusEventForm, AddNewStatusEvent, SettingsTopBar }
}

})